'use client';

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ensureFontReady, measureText } from '../lib/tapestryText';
import { wrapAcrossGuides, lineHeightScalar, TEXTPATH_FIT } from '../lib/curatedText';
import { phraseNotUsed } from '../lib/fillSlots';
import { useTapestryStagger } from '../lib/tapestryStagger';
import { useSeenReveal, normalizeSeenKey } from '../lib/seenReveal';
import {
  LINE_DURATION_MS,
  PEN_SIZE_SCALE,
  DUST_SPAWN_INTERVAL_MS,
  DUST_LIFETIME_MS,
  spawnDustParticle,
  advanceParticles,
  penRockOffset,
} from '../lib/penReveal';
import { PenFilter, DustParticles, PenSprite } from './PenDust';

const DEFAULT_FONT_FAMILY = 'Hand1-Regular';
const DUST_FILTER_ID = 'curated-dust-filter';
const PEN_SPRITE_FILTER_ID = 'curated-pen-sprite-filter';

// Over-long text: shrink the font toward fitting (so it isn't scrunched) but not
// below MIN_FONT_SCALE of the tier size (so it doesn't get too small). The
// relative cap keeps text from looking too small; ABS_MIN_FONT is an absolute
// floor. fitWrap's `Math.min(startFont, ...)` guard means a tier smaller than
// ABS_MIN_FONT simply isn't allowed to shrink (never grown), so the floor can
// sit above the smallest tiers safely. We keep these high on purpose: rather than
// shrink a too-long sentence to an unreadable size, the layout step prefers a
// shorter *fallback phrase* that fits (see the candidate loop below). MAX_FILL =
// how full of the (margined) guide before shrinking.
const MIN_FONT_SCALE = 0.6;
const ABS_MIN_FONT = 24;
const MAX_FILL = 1.0;
// Single words don't wrap, so on a large-tier slot (word=72, title=64, h1=150)
// they render oversized. Cap their starting size; multi-word text keeps its tier
// and shrinks via fitWrap instead.
const SINGLE_WORD_MAX_FONT = 44;

// Glyphs whose position falls past the end of an SVG textPath are not rendered,
// so an over-long line gets its trailing letters clipped. Curated text is
// left-anchored at the guide start, so overrun only happens at the END; we extend
// each guide this fraction of its length past its end (straight runway along the
// end tangent) and ride text on the longer path, so overrunning letters land on
// the runway and stay visible. Fitting still targets the authored length, so this
// only catches the residual overrun. Kept SMALL so the runway doesn't push text
// into neighboring slots — the render-time clamp (below) guarantees the line fits
// within it.
const GUIDE_EXTEND = 0.04;
// Visible path length relative to the authored guide length (end runway only —
// left-anchored text can only overrun the end).
const RENDERED_SCALE = 1 + GUIDE_EXTEND;
// The no-squeeze runway budget: the fill ratio (worst line's measured width /
// margined-authored length) at which a line exactly fills the visible path
// without the clamp compressing it. Not used as an acceptance test on its own —
// it's the base ACCEPT_FILL_COMPRESSED widens by the compression floor below.
const ACCEPT_FILL = RENDERED_SCALE / TEXTPATH_FIT;
// Horizontal-compression floor. The render-time clamp squeezes an over-long
// line (via textLength + lengthAdjust="spacingAndGlyphs") so its tail doesn't
// clip off the path end; but compressing glyphs narrower than this turns text
// into an illegible horizontal blur. So we never squeeze below MIN_HSCALE, and
// the layout step treats "fits within a <=20% squeeze" as acceptable — walking
// down the ranked candidates for the highest-ranked phrase that fits inside
// that budget, rather than jamming a too-long primary in and crushing it.
const MIN_HSCALE = 0.8;
// A phrase is acceptable if its worst line fits once the clamp is allowed to
// compress it down to MIN_HSCALE (i.e. the fill ratio stays within the runway
// budget divided by the compression floor). Above this the line would have to
// compress past MIN_HSCALE, so the layout step prefers the next candidate.
const ACCEPT_FILL_COMPRESSED = ACCEPT_FILL / MIN_HSCALE;

// Append straight runway past the END of `el`'s authored path (currently
// `authoredD`, already measured to `total`), along the end tangent. The authored
// start is kept verbatim so left-anchored text begins exactly at the guide's
// start point; only the end is extended to catch residual overrun.
function extendGuide(el, total, authoredD, ext) {
  const eps = Math.min(1, total * 0.02) || 1;
  const pe = el.getPointAtLength(total);
  const pb = el.getPointAtLength(Math.max(0, total - eps));
  const dx = pe.x - pb.x;
  const dy = pe.y - pb.y;
  const m = Math.hypot(dx, dy) || 1;
  const eX = (pe.x + (dx / m) * ext).toFixed(2);
  const eY = (pe.y + (dy / m) * ext).toFixed(2);
  return `${authoredD} L ${eX} ${eY}`;
}

// Worst-case fill ratio across lines at a given font size (text width vs the
// margined guide length). > 1 means a line overruns its authored span.
function worstFill(lines, fontSize, measure) {
  let maxRatio = 0;
  for (const ln of lines) {
    maxRatio = Math.max(maxRatio, measure(ln.text, fontSize) / Math.max(1, ln.length * TEXTPATH_FIT));
  }
  return maxRatio;
}

function fitWrap(phrase, lineGuides, tierFont, measure, { fixedFont = false } = {}) {
  const singleWord = !/\s/.test((phrase || '').trim());
  // `fixedFont` (scene text, TT-1046): the tier IS the intended size — a
  // one-word title or headline must not be capped like a slot-filling lemma.
  const startFont = singleWord && !fixedFont ? Math.min(tierFont, SINGLE_WORD_MAX_FONT) : tierFont;
  const minFont = Math.min(startFont, Math.max(ABS_MIN_FONT, startFont * MIN_FONT_SCALE));
  let fontSize = startFont;
  let lines = wrapAcrossGuides(phrase, lineGuides, fontSize, measure);
  let maxRatio = worstFill(lines, fontSize, measure);
  for (let i = 0; i < 4; i++) {
    if (maxRatio <= MAX_FILL || fontSize <= minFont) break;
    fontSize = Math.max(minFont, (fontSize * MAX_FILL) / maxRatio);
    lines = wrapAcrossGuides(phrase, lineGuides, fontSize, measure);
    maxRatio = worstFill(lines, fontSize, measure);
  }
  // maxRatio = worst line's fill vs its margined guide. The caller accepts the
  // phrase if it's within ACCEPT_FILL_COMPRESSED (fits under the MIN_HSCALE
  // squeeze budget), else moves on to the next candidate.
  return { fontSize, lines, maxRatio };
}

/**
 * One curated line: text written along its authored path with the pen-trace
 * reveal — words fade in as a pen walks the path, chalk dust trailing. Gated on
 * the stagger queue so lines (and symbols) draw in sequence. The line is colored
 * from the background data; no chalk-yellow flash.
 */
const CuratedLine = memo(function CuratedLine({
  line,
  color,
  fontFamily,
  dropShadow,
  getPath,
  playReveal = true,
  onStart,
}) {
  const tpRef = useRef(null);
  const idRef = useRef(0);
  const [anim, setAnim] = useState({ progress: 0, particles: [] });
  // Already-introduced text (playReveal === false) skips the stagger queue and
  // pen-trace: it shows fully drawn immediately (TT-961).
  const [started, setStarted] = useState(!playReveal);
  const stagger = useTapestryStagger();

  const words = useMemo(() => line.text.split(/\s+/).filter(Boolean), [line.text]);
  const lineHeight = line.fontSize * 1.05;
  const penSize = lineHeight * PEN_SIZE_SCALE;

  // Where the (left-anchored) text sits along the path, so the pen walks the
  // text, not the whole path. Text starts at the guide's start (offset 0) and
  // runs `textWidth` along it.
  const extent = () => {
    const path = getPath(line.pathId);
    let total = line.length;
    try {
      if (path) total = path.getTotalLength();
    } catch {
      /* keep parsed length */
    }
    return { path, total, startLen: 0, endLen: Math.min(total, line.textWidth || 0) };
  };

  const penAt = (progress) => {
    const { path, total, startLen, endLen } = extent();
    if (!path) return null;
    const L = startLen + (endLen - startLen) * progress;
    try {
      const a = path.getPointAtLength(L);
      const b = path.getPointAtLength(Math.min(total, L + 1));
      return {
        x: a.x,
        y: a.y + line.dy,
        angle: (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI + penRockOffset(progress),
      };
    } catch {
      return null;
    }
  };

  // Fit clamp: if a line still overruns the visible path (authored span + runway),
  // compress it back toward fitting so the end glyphs aren't clipped off the path
  // ends — but never squeeze narrower than MIN_HSCALE, since crushing glyphs past
  // that turns the line into an illegible blur. A line long enough to still
  // overrun at MIN_HSCALE keeps that floor and lets its tail run off the end
  // rather than compressing every glyph to mush; upstream phrase selection makes
  // that case rare by preferring a shorter phrase that fits the budget.
  useEffect(() => {
    const el = tpRef.current;
    if (!el) return;
    el.removeAttribute('textLength');
    el.removeAttribute('lengthAdjust');
    let measured = 0;
    try {
      measured = el.getComputedTextLength();
    } catch {
      return;
    }
    const visible = line.length * RENDERED_SCALE * 0.98; // leave a sliver of tip margin
    if (line.length > 0 && measured > visible) {
      const target = Math.max(visible, measured * MIN_HSCALE); // cap compression at MIN_HSCALE
      el.setAttribute('textLength', target);
      el.setAttribute('lengthAdjust', 'spacingAndGlyphs');
    }
  }, [line.text, line.fontSize, line.length]);

  // rAF reveal, started by the stagger queue (or immediately without one).
  useEffect(() => {
    // Seen text doesn't replay the pen-trace (TT-961) — no queue slot, no rAF.
    if (!playReveal) return;
    let raf = null;
    let startT = null;
    let lastSpawn = 0;
    let cancelled = false;
    const ext = extent();

    let signaled = false;
    const step = (t) => {
      if (cancelled) return;
      if (startT == null) {
        startT = t;
        lastSpawn = t - DUST_SPAWN_INTERVAL_MS;
        // Signal the block's draw-start HERE — on the first real animation frame
        // — not in begin(). Under React Strict Mode the stagger's cancel-advances
        // behavior fires queued begin()s during the mount/cleanup churn, which
        // would release every outgoing block at once; but those rAFs are
        // cancelled before step() runs, so signaling here fires only for the
        // animation that actually draws, at its true (staggered) time (TT-961).
        if (!signaled) {
          signaled = true;
          onStart?.();
        }
      }
      const progress = Math.min(1, (t - startT) / LINE_DURATION_MS);

      let spawned = null;
      if (progress < 1 && ext.path && t - lastSpawn >= DUST_SPAWN_INTERVAL_MS * (0.7 + Math.random() * 0.9)) {
        lastSpawn = t;
        const pt = penAt(progress);
        if (pt) spawned = spawnDustParticle(idRef.current++, t, pt.x, pt.y, lineHeight);
      }

      setAnim((prev) => ({ progress, particles: advanceParticles(prev.particles, t, spawned) }));

      if (t - startT < LINE_DURATION_MS + DUST_LIFETIME_MS) raf = requestAnimationFrame(step);
    };

    const begin = () => {
      if (cancelled) return;
      setStarted(true);
      raf = requestAnimationFrame(step); // onStart fires from step()'s first frame
    };
    const reservation = stagger ? stagger.schedule(LINE_DURATION_MS, begin) : (begin(), null);

    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      reservation?.cancel?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { progress, particles } = anim;
  // Seen text is pinned fully revealed with no pen; new text follows progress.
  const revealProgress = playReveal ? progress : 1;
  const pen = playReveal && started && progress > 0 && progress < 1 ? penAt(progress) : null;

  return (
    <g>
      <text
        fontSize={`${line.fontSize}px`}
        fontFamily={`'${fontFamily}', sans-serif`}
        textAnchor="start"
        fill={color}
        transform={line.dy ? `translate(0, ${line.dy})` : undefined}
        style={dropShadow ? { filter: `drop-shadow(1px 1px 2px ${dropShadow})` } : undefined}
      >
        <textPath ref={tpRef} href={`#${line.pathId}`} startOffset="0">
          {words.map((w, wi) => {
            const visible = started && revealProgress >= (wi + 0.5) / words.length;
            return (
              <tspan key={wi} opacity={visible ? 1 : 0}>
                {w}
                {wi < words.length - 1 ? ' ' : ''}
              </tspan>
            );
          })}
        </textPath>
      </text>

      {started && <DustParticles particles={particles} filterId={DUST_FILTER_ID} />}

      {pen && <PenSprite x={pen.x} y={pen.y} angle={pen.angle} size={penSize} filterId={PEN_SPRITE_FILTER_ID} />}
    </g>
  );
});

/**
 * One curated text block (all wrapped lines of a single slot's sentence).
 * Consults the scene registry once at mount so the whole block reveals as a
 * unit: if its sentence was already introduced (generatively or in an earlier
 * slot), none of its lines pen-trace — they render fully drawn (TT-961). The
 * parent keys the block on textId + phrase, so a genuine sentence change
 * remounts it and re-consults.
 */
const CuratedTextBlock = memo(function CuratedTextBlock({
  block,
  getPath,
  exiting = false,
  readyToFade = false,
  fastExit = false,
  onStart,
}) {
  const seen = useSeenReveal();
  // Consulted once, at mount (the registry answers the same within a frame).
  const [playAtMount] = useState(() => (seen ? seen.reveal(block.phrase) : true));
  // A block that's leaving never pen-draws — it fades out via opacity.
  const playReveal = playAtMount && !exiting && !seen?.static;

  // Report this slot's draw-start so the OUTGOING block it replaced knows when
  // to begin fading (holds the old text visible until now — TT-961). A seen
  // block appears immediately on mount; a new block reports when its first line
  // begins the pen-trace (see the first CuratedLine's onStart below).
  const startedRef = useRef(false);
  const signalStart = useCallback(() => {
    if (startedRef.current || exiting) return;
    startedRef.current = true;
    onStart?.(block.textId);
  }, [exiting, onStart, block.textId]);
  const seenIncoming = !exiting && !playReveal;
  useEffect(() => {
    if (seenIncoming) signalStart();
  }, [seenIncoming, signalStart]);

  // Blocks that don't pen-draw (already seen, or exiting) are crossfaded via
  // opacity: seen blocks fade in on arrival, exiting blocks fade out (TT-961).
  // New blocks skip the fade — their pen-trace reveal is the intro. An exiting
  // block holds at full opacity until its slot is `readyToFade`.
  // A static registry (scene previews) renders straight: no rAF-driven fade.
  const fade = !playReveal && !seen?.static;
  const [appeared, setAppeared] = useState(!fade);
  useEffect(() => {
    if (!fade || exiting) return;
    const id = requestAnimationFrame(() => setAppeared(true));
    return () => cancelAnimationFrame(id);
  }, [fade, exiting]);
  const opacity = exiting ? (readyToFade ? 0 : 1) : fade ? (appeared ? 1 : 0) : 1;
  // Scene-switch exits override the default 550ms crossfade with a quick fade so
  // the old scene's text doesn't linger over the new one (inline transition wins
  // over the .tapestry-seen-fade class).
  const style =
    exiting && fastExit ? { opacity, transition: `opacity ${CURATED_SCENE_EXIT_MS}ms ease-out` } : { opacity };

  return (
    <g className={fade ? 'tapestry-seen-fade' : undefined} style={style}>
      {block.lines.map((ln, i) => (
        <CuratedLine
          // Key on line content so a wrap change swaps the line; playReveal
          // (inherited from the block) keeps seen text from redrawing.
          key={`${i}:${ln.text}`}
          line={ln}
          color={block.color}
          fontFamily={block.fontFamily}
          dropShadow={block.dropShadow}
          getPath={getPath}
          playReveal={playReveal}
          // Only the first line reports the block's draw-start.
          onStart={i === 0 ? signalStart : undefined}
        />
      ))}
    </g>
  );
});

// How long a curated block lingers, fading out, once released. Matches the
// .tapestry-seen-fade CSS so the outgoing block's fade-out crossfades with the
// incoming block's fade-in.
const CURATED_EXIT_MS = 550;
// On a whole-scene (background) switch the outgoing text has no same-slot
// successor to crossfade with — the new scene mostly pops straight in — so the
// full 550ms fade just reads as the old text lingering over the new. Scene-exit
// blocks fade out quickly instead (see `fastExit`).
const CURATED_SCENE_EXIT_MS = 220;
// Safety net: never hold an outgoing block longer than this waiting for its
// release trigger (e.g. a transition that stalls before drawing new content).
const MAX_HOLD_MS = 4000;

function nowMs() {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

const blockKey = (b) => `${b.textId}:${normalizeSeenKey(b.phrase)}`;

/**
 * Retain curated blocks after they leave so they can fade out instead of cutting,
 * and — crucially — RELEASE each one exactly when its replacement is about to draw
 * in, so a textpath never blinks empty before new content appears (TT-961).
 *
 * The returned `notifyDraw(textId)` is called by a block the moment it begins
 * drawing (pen-begin for new text; mount for already-seen text). Each such draw
 * releases EXACTLY ONE outgoing block, choosing:
 *   - the outgoing block in the SAME slot, if any (spatially-exact handoff — the
 *     old text on a guide stays until the new text starts on that same guide);
 *   - else the oldest ORPHANED outgoing block (its slot has no replacement — e.g.
 *     a background switch), so the scene stays populated 1-in-1-out instead of
 *     blanking then refilling.
 * Because a draw releases at most one block, old text never fades en masse; it
 * leaves in lockstep with new text arriving. `readyAt` (set here) is the single
 * source of truth for "this block is now fading". A MAX_HOLD_MS fallback releases
 * anything still held if the incoming draws stall.
 */
function useCuratedExitFade(blocks, sceneKey) {
  const [combined, setCombined] = useState([]);
  const exitSeqRef = useRef(0);

  // Diff: keep current blocks; carry departed ones as `exiting` with a monotonic
  // exit order for oldest-first orphan release.
  //
  // Each block carries the scene (background) it was laid under (`sceneId`, set in
  // the layout pass). A departing block whose scene differs from the one now
  // showing (`sceneKey`) left because the whole background switched — there is no
  // same-slot successor to hand off to, so release it IMMEDIATELY with a fast fade
  // (`fastExit`) instead of holding it for a 1-in-1-out draw that never comes (or
  // the multi-second MAX_HOLD_MS fallback). A within-scene phrase change keeps the
  // scene equal, so it still uses the gentle 1-in-1-out crossfade below.
  //
  // Tagging at layout time (not stamping here) keeps this correct across two edge
  // cases: (1) `blocks` is recomputed asynchronously AFTER `sceneKey` changes, but
  // each block's `sceneId` travels with it, so departures are classified by where
  // the block actually came from — not by whichever `sceneKey` was current; (2) a
  // block that persists across a switch (shared layout, same slot+phrase) is
  // re-laid under the new scene, so a LATER within-scene phrase change is
  // correctly treated as within-scene.
  useEffect(() => {
    setCombined((prev) => {
      const incoming = new Set(blocks.map(blockKey));
      const fresh = blocks.map((b) => ({ ...b, exiting: false }));
      const now = nowMs();
      const carried = [];
      for (const item of prev) {
        if (incoming.has(blockKey(item))) continue;
        const crossScene = item.sceneId !== sceneKey;
        if (item.exiting) {
          // Already leaving when the background switched. If it's from the old
          // scene and not already fast-exiting, promote it now — whether it was
          // still held (readyAt null) OR already crossfading slowly (readyAt set)
          // — so it doesn't linger the full 550ms over the new scene. Keep an
          // existing readyAt so a mid-fade block doesn't restart its clock.
          carried.push(crossScene && !item.fastExit ? { ...item, readyAt: item.readyAt ?? now, fastExit: true } : item);
          continue;
        }
        carried.push({
          ...item,
          exiting: true,
          leftAt: now,
          exitSeq: exitSeqRef.current++,
          readyAt: crossScene ? now : null, // cross-scene: fade out right away
          fastExit: crossScene,
        });
      }
      return [...fresh, ...carried];
    });
  }, [blocks, sceneKey]);

  const notifyDraw = useCallback((textId) => {
    setCombined((curr) => {
      const now = nowMs();
      // Prefer the exact same-slot handoff.
      const sameSlot = curr.filter((c) => c.exiting && c.readyAt == null && c.textId === textId);
      if (sameSlot.length) {
        const release = new Set(sameSlot.map(blockKey));
        return curr.map((c) => (release.has(blockKey(c)) && c.readyAt == null ? { ...c, readyAt: now } : c));
      }
      // Otherwise retire the oldest orphaned block (slot has no replacement).
      const currentTextIds = new Set(curr.filter((c) => !c.exiting).map((c) => c.textId));
      const orphan = curr
        .filter((c) => c.exiting && c.readyAt == null && !currentTextIds.has(c.textId))
        .sort((a, b) => a.exitSeq - b.exitSeq)[0];
      if (!orphan) return curr;
      const okey = blockKey(orphan);
      return curr.map((c) => (blockKey(c) === okey && c.readyAt == null ? { ...c, readyAt: now } : c));
    });
  }, []);

  // Max-hold fallback + prune once the fade window has elapsed.
  useEffect(() => {
    const id = setInterval(() => {
      setCombined((curr) => {
        const now = nowMs();
        let changed = false;
        const next = [];
        for (const c of curr) {
          if (!c.exiting) {
            next.push(c);
            continue;
          }
          let item = c;
          if (item.readyAt == null && now - item.leftAt >= MAX_HOLD_MS) {
            item = { ...item, readyAt: now };
            changed = true;
          }
          const fadeMs = item.fastExit ? CURATED_SCENE_EXIT_MS : CURATED_EXIT_MS;
          if (item.readyAt != null && now - item.readyAt >= fadeMs) {
            changed = true; // fade finished — drop it
            continue;
          }
          next.push(item);
        }
        return changed ? next : curr;
      });
    }, 80);
    return () => clearInterval(id);
  }, []);

  return [combined, notifyDraw];
}

/**
 * Renders curated-layout text along the layout's authored textpath guides.
 *
 * Text is placed on the AUTHORED path verbatim (`<textPath href="#guide">`), so
 * any path type — lines, quadratics, cubic beziers — lands exactly where the
 * background defines it (the browser does the geometry). Real path lengths come
 * from `getTotalLength`, so wrapping/fitting matches what's drawn. Each line
 * draws in with the pen-trace reveal (see CuratedLine).
 *
 * The SVG viewBox is the layout coordinate space (1800 × 1800*heightRatio); the
 * host sizes the canvas to the background aspect so this overlays it 1:1.
 */
// `fillHost`: size the SVG 100% of its positioned host instead of the pixel
// canvas size, so it rides a CSS-transitioned host (SceneCanvas camera ease);
// `canvasHeight` is still required — it keys the measurement pass.
function CuratedText({ texts, viewBox, canvasWidth, canvasHeight, sceneId, fillHost = false }) {
  const pathRefs = useRef(new Map()); // pathId -> <path> element (for getTotalLength/getPointAtLength)
  const seen = useSeenReveal();
  const [laid, setLaid] = useState({ source: null, blocks: [] });

  // Distinct guide paths to render (and measure). Aggregate: one per guide;
  // single-string textpath: just the one guide (reused over stacked lines).
  const guidePaths = useMemo(() => {
    const out = [];
    for (const t of texts || []) {
      const n = t.single ? 1 : t.guides.length;
      for (let gi = 0; gi < n; gi++) {
        const g = t.guides[gi];
        if (g) out.push({ id: `cg-${t.id}-${gi}`, d: g.d });
      }
    }
    return out;
  }, [texts]);

  // After the guide paths are in the DOM and fonts are ready, measure real path
  // lengths, wrap + shrink-to-fit each text, and lay out the lines.
  useEffect(() => {
    let cancelled = false;
    const families = [...new Set((texts || []).map((t) => t.fontFamily || DEFAULT_FONT_FAMILY))];
    Promise.all(families.map((f) => ensureFontReady(f))).then(() => {
      if (cancelled) return;
      // Measure the authored length (reset first so a re-run doesn't read a
      // previously-extended path), then give the rendered path its runway. Fit
      // uses the returned authored length; the textPath rides the extended path.
      const prepGuide = (id, authoredD) => {
        const el = pathRefs.current.get(id);
        if (!el) return 0;
        try {
          el.setAttribute('d', authoredD);
          const total = el.getTotalLength();
          if (total > 0) el.setAttribute('d', extendGuide(el, total, authoredD, GUIDE_EXTEND * total));
          return total;
        } catch {
          return 0;
        }
      };

      const out = [];
      // Sentences already placed in this pass — no two slots may show the same
      // (or a substring-overlapping) sentence. fillSlots dedups each slot's
      // *primary*, but fallback lists can overlap across slots, so a fit-driven
      // swap here could otherwise reintroduce a duplicate.
      const usedPhrases = [];
      for (const t of texts || []) {
        const fontFamily = t.fontFamily || DEFAULT_FONT_FAMILY;
        const measure = (txt, fs) => measureText(txt, fs, fontFamily).width;
        const n = t.single ? 1 : t.guides.length;

        const guides = [];
        // { id, d } per guide, kept on the block so it can still render its
        // textpaths while displayed without its guide in the live set (see
        // extraGuidePaths) — fading out after leaving a slot, or lingering from
        // the old background mid-switch.
        const guideDefs = [];
        for (let gi = 0; gi < n; gi++) {
          const g = t.guides[gi];
          if (!g) continue;
          const id = `cg-${t.id}-${gi}`;
          guides.push({ ...g, length: prepGuide(id, g.d) || g.length });
          guideDefs.push({ id, d: g.d });
        }
        if (!guides.length) continue;

        const lineGuides = t.single ? Array.from({ length: t.maxLines }, () => guides[0]) : guides;
        // Candidates are ranked best-first (primary, then score-ordered
        // fallbacks). Walk them and take the highest-ranked phrase that fits
        // within the compression budget (<=20% squeeze) — so a phrase that
        // doesn't fit yields to the *next-best* phrase that does, rather than
        // being crushed to an illegible blur. Skip any candidate that repeats a
        // sentence already placed in another slot this pass. If nothing fits
        // even within the budget, leave the slot EMPTY — an over-mangled line is
        // worse than a blank guide.
        const candidates = [t.phrase, ...(t.phraseFallbacks || [])];
        let chosen = null;
        let chosenPhrase = null;
        for (const cand of candidates) {
          // Scene text (TT-1046) sets allowDuplicate: a fragment legitimately
          // repeats words of its sub-head ("CAPACITY" / "CREATE A CAPACITY MAP").
          if (cand && !t.allowDuplicate && !phraseNotUsed(cand, usedPhrases)) continue; // would duplicate another slot
          const fit = fitWrap(cand, lineGuides, t.fontSize, measure, { fixedFont: !!t.fixedFont });
          if (fit.maxRatio <= ACCEPT_FILL_COMPRESSED) {
            chosen = fit;
            chosenPhrase = cand;
            break;
          } // fits within the squeeze budget
        }
        if (!chosen) continue; // nothing fit — render nothing rather than mangle
        if (chosenPhrase && !t.allowDuplicate) usedPhrases.push(chosenPhrase);
        const { fontSize, lines } = chosen;
        const lh = fontSize * lineHeightScalar(fontSize);

        out.push({
          textId: t.id,
          // The background this block was laid under — its exit-fade
          // classification reads this (a departing block whose scene differs from
          // the one now showing means the whole background switched). Stamped here
          // so it travels with the block and survives the async layout gap.
          sceneId,
          // The whole chosen sentence — the scene-registry identity for this
          // block (TT-961). Matches the same sentence shown generatively, so it
          // doesn't re-pen-trace when it lands in a curated slot.
          phrase: chosenPhrase,
          guides: guideDefs, // kept so the block can render while exit-fading
          color: t.color,
          fontFamily,
          dropShadow: t.dropShadow,
          lines: lines.map((ln, i) => {
            const text = t.uppercase ? ln.text.toUpperCase() : ln.text;
            return {
              pathId: `cg-${t.id}-${t.single ? 0 : Math.min(i, n - 1)}`,
              text,
              fontSize,
              dy: t.single ? i * lh : 0,
              length: ln.length,
              textWidth: measure(text, fontSize),
            };
          }),
        });
      }
      if (!cancelled) setLaid({ source: texts, blocks: out });
    });
    return () => {
      cancelled = true;
    };
  }, [texts, viewBox, canvasHeight, sceneId]);

  const getPath = useMemo(() => (id) => pathRefs.current.get(id), []);

  // Current blocks plus recently-departed ones still fading out (crossfade). Each
  // outgoing block is held until `notifyDraw` releases it (sets readyAt) — when
  // its slot's replacement, or the next new content, begins drawing.
  const [fading, notifyDraw] = useCuratedExitFade(laid.blocks, sceneId);
  const displayed = seen?.static ? (laid.source === texts ? laid.blocks : []) : fading;

  if (!viewBox || !canvasHeight) return null;

  // Extra guide defs for any displayed block whose guide isn't in the current
  // measurement set (`guidePaths`, derived from `texts`). This covers two cases:
  // a departed block fading out in a now-empty slot, AND — crucially — a block
  // still displayed from the OLD background during a switch. `texts` (and thus
  // `guidePaths`) updates synchronously when the background changes, but the
  // displayed blocks come from `laid`, which lags by the async layout pass; for
  // that gap the old text is still on screen, so without re-emitting its guides
  // it would lose its path and blink out before it's reclassified as exiting.
  // Deduped against the measurement set (a refilled slot already provides the
  // same-id guide) to avoid duplicate SVG ids.
  const extraGuidePaths = [];
  const emittedGuideIds = new Set(guidePaths.map((g) => g.id));
  for (const b of displayed) {
    if (!b.guides) continue;
    for (const g of b.guides) {
      if (emittedGuideIds.has(g.id)) continue;
      emittedGuideIds.add(g.id);
      extraGuidePaths.push(g);
    }
  }

  return (
    <svg
      width={fillHost ? '100%' : canvasWidth}
      height={fillHost ? '100%' : canvasHeight}
      viewBox={`0 0 ${viewBox.width} ${viewBox.height}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}
    >
      <defs>
        <PenFilter id={DUST_FILTER_ID} />
        <PenFilter id={PEN_SPRITE_FILTER_ID} outline />
        {guidePaths.map((g) => (
          <path
            key={g.id}
            id={g.id}
            d={g.d}
            fill="none"
            stroke="none"
            ref={(el) => {
              if (el) pathRefs.current.set(g.id, el);
              else pathRefs.current.delete(g.id);
            }}
          />
        ))}
        {/* Static guide defs kept alive for displayed blocks whose guide isn't in
            the current measurement set (exit-fading blocks, and old-background
            blocks still on screen during a switch — see extraGuidePaths). */}
        {extraGuidePaths.map((g) => (
          <path key={g.id} id={g.id} d={g.d} fill="none" stroke="none" />
        ))}
      </defs>
      {displayed.map((t) => (
        // Key on textId + phrase so a genuine sentence change remounts the block
        // (re-consulting the registry); a re-wrap of the same sentence keeps the
        // block and its "already seen" verdict. Exiting blocks keep their key so
        // they persist (fading out) instead of remounting.
        <CuratedTextBlock
          key={`${t.textId}:${normalizeSeenKey(t.phrase)}`}
          block={t}
          getPath={getPath}
          exiting={t.exiting}
          readyToFade={t.exiting && t.readyAt != null}
          fastExit={!!t.fastExit}
          onStart={notifyDraw}
        />
      ))}
    </svg>
  );
}

export default memo(CuratedText);
